import React from 'react';
import { Box, Paper, ButtonBase, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import RadioIcon from '@mui/icons-material/Radio';
import AutoStoriesIcon from '@mui/icons-material/AutoStories'; // Journal
import PersonIcon from '@mui/icons-material/Person';
import GrainIcon from '@mui/icons-material/Grain'; // Matrix/Labs
import { useLanguage } from '../../context/LanguageContext';

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { language, toggleLanguage, t } = useLanguage();

  const navItems = [
    { label: t('nav_home'), icon: <HomeIcon />, path: '/' },
    { label: t('nav_signal'), icon: <RadioIcon />, path: '/black-star-signal' },
    { label: t('nav_journal'), icon: <AutoStoriesIcon />, path: '/journal' },
    { label: t('nav_labs'), icon: <GrainIcon />, path: '/creative-lab' },
    { label: t('nav_profile'), icon: <PersonIcon />, path: '/profile' },
  ];

  if (!isMobile) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 'max(8px, env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 16px)',
        zIndex: 1150,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Paper
        className="glass-panel"
        sx={{
          borderRadius: '16px',
          padding: '4px',
          display: 'flex',
          gap: 0,
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          width: '100%',
          maxWidth: '440px',
          minWidth: 0,
        }}
      >
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ButtonBase
              key={item.label}
              onClick={() => navigate(item.path)}
              aria-label={item.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                minHeight: '56px',
                px: 0.25,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                color: isActive ? '#00f0fc' : 'rgba(255, 255, 255, 0.4)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', lineHeight: 0 }}>
                {item.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  width: '100%',
                  mt: 0.5,
                  px: 0.1,
                  fontSize: { xs: '0.62rem', sm: '0.72rem' },
                  lineHeight: 1.15,
                  fontWeight: isActive ? 700 : 500,
                  textTransform: 'none',
                  letterSpacing: { xs: '0.01em', sm: '0.05em' },
                  fontFamily: '"Manrope", sans-serif',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {item.label}
              </Typography>
              {isActive && (
                <Box
                  sx={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: '#00f0fc',
                    marginTop: '4px',
                    boxShadow: '0 0 8px #00f0fc',
                  }}
                />
              )}
            </ButtonBase>
          );
        })}

        {/* Language Toggle Button */}
        <ButtonBase
          onClick={toggleLanguage}
          aria-label={t('nav_language')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            minHeight: '56px',
            borderRadius: '12px',
            cursor: 'pointer',
            color: '#D37633', // SIGNAL_ORANGE
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              color: '#00f0fc',
              backgroundColor: 'rgba(0, 240, 252, 0.05)',
            }
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', fontFamily: '"Manrope", sans-serif' }}>
            {language === 'en' ? 'FR' : 'EN'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              fontSize: { xs: '0.52rem', sm: '0.6rem' },
              lineHeight: '12px',
              fontWeight: 700,
              textTransform: 'none',
              letterSpacing: { xs: '0.02em', sm: '0.15em' },
              fontFamily: '"Manrope", sans-serif',
              opacity: 0.7,
            }}
          >
            {t('nav_language_short')}
          </Typography>
        </ButtonBase>
      </Paper>
    </Box>
  );
};

export default MobileBottomNav;
